import React, { useEffect, useState } from "react";
import { Box, Button, Card, Grid, Typography, CircularProgress, Alert } from "@mui/material";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import useFetchObjects from "../../api/useFetchObjects";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthContext";
import { ResponsivePie } from "@nivo/pie";
import { ResponsiveBar } from "@nivo/bar";

const COLORS = ["#4c6ef5", "#22c55e", "#fbbc04", "#ec4899", "#06b6d4"];

const Dashboard = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const user = auth?.user;
  const token = user?.token;
  const themeDirection = localStorage.getItem("direction");

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const formattedStart = startDate?.format("YYYY-MM-DD") || "";
  const formattedEnd = endDate?.format("YYYY-MM-DD") || "";
  const [dateError, setDateError] = useState(null);

  useEffect(() => {
    if (startDate && endDate && endDate.isBefore(startDate)) {
      setDateError("Invalid date range: End date must be after start date.");
    } else {
      setDateError(null);
    }
  }, [startDate, endDate]);

  const { data, isLoading, isError, error } = useFetchObjects(
    ["dashboardReport", formattedStart, formattedEnd],
    `shop/financial-report/?start_date=${formattedStart}&end_date=${formattedEnd}`,
    token
  );

  const pieDataFrom = (type) => {
    if (!data || !data[type] || data[type].length === 0) {
      return [{ id: "No Data", label: "No Data", value: 1 }];
    }
    return data[type].map((item) => ({
      id: item.currency,
      label: item.currency,
      value: Math.abs(Number(item.amount)) || 0.01,
    }));
  };

  const barData = (() => {
    if (!data?.total_sales || !data?.total_purchases) {
      return [{ currency: "No Data", sales: 0, purchases: 0 }];
    }
    const map = (arr) => Object.fromEntries(arr.map((i) => [i.currency, Math.abs(Number(i.amount))]));
    const purchasesMap = map(data.total_purchases);
    const salesMap = map(data.total_sales);
    const allCurrencies = [...new Set([...Object.keys(purchasesMap), ...Object.keys(salesMap)])];
    if (allCurrencies.length === 0) return [{ currency: "No Data", sales: 0, purchases: 0 }];
    return allCurrencies.map((currency) => ({
      currency,
      sales: salesMap[currency] || 0,
      purchases: purchasesMap[currency] || 0,
    }));
  })();

  const paymentData = (() => {
    if (!data?.total_sales_paid || !data?.total_purchases_paid) {
      return [{ currency: "No Data", sales_paid: 0, purchases_paid: 0 }];
    }
    const map = (arr) => Object.fromEntries(arr.map((i) => [i.currency, Math.abs(Number(i.amount))]));
    const purchasesPaidMap = map(data.total_purchases_paid);
    const salesPaidMap = map(data.total_sales_paid);
    const allCurrencies = [...new Set([...Object.keys(purchasesPaidMap), ...Object.keys(salesPaidMap)])];
    if (allCurrencies.length === 0) return [{ currency: "No Data", sales_paid: 0, purchases_paid: 0 }];
    return allCurrencies.map((currency) => ({
      currency,
      sales_paid: salesPaidMap[currency] || 0,
      purchases_paid: purchasesPaidMap[currency] || 0,
    }));
  })();

  const renderSummaryCard = (title, items = [], subtitle = null, isCalculated = false, incomes = [], outcomes = [], expenses = [], payrolls = []) => {
    let displayItems = items;
    
    // If this is a calculated field (Total Benefit), calculate it properly
    if (isCalculated && title === "Total Benefit") {
      const incomeMap = {};
      const outcomeMap = {};
      const expenseMap = {};
      const payrollMap = {};
      
      // Build income map
      (incomes || []).forEach(item => {
        const currency = item.currency || 'AF';
        incomeMap[currency] = (incomeMap[currency] || 0) + (Number(item.amount) || 0);
      });
      
      // Build outcome map
      (outcomes || []).forEach(item => {
        const currency = item.currency || 'AF';
        outcomeMap[currency] = (outcomeMap[currency] || 0) + (Number(item.amount) || 0);
      });
      
      // Build expense map
      (expenses || []).forEach(item => {
        const currency = item.currency || 'AF';
        expenseMap[currency] = (expenseMap[currency] || 0) + (Number(item.amount) || 0);
      });
      
      // Build payroll map
      (payrolls || []).forEach(item => {
        const currency = item.currency || 'AF';
        payrollMap[currency] = (payrollMap[currency] || 0) + (Number(item.amount) || 0);
      });
      
      // Calculate benefit: Total Incomes + Outcome - Expenses - Payroll
      const allCurrencies = [...new Set([
        ...Object.keys(incomeMap), 
        ...Object.keys(outcomeMap),
        ...Object.keys(expenseMap),
        ...Object.keys(payrollMap)
      ])];
      
      displayItems = allCurrencies.map(currency => ({
        currency,
        amount: (incomeMap[currency] || 0) + (outcomeMap[currency] || 0) - (expenseMap[currency] || 0) - (payrollMap[currency] || 0)
      }));
    }
    
    return (
      <Card
        elevation={3}
        sx={{
          p: 3,
          borderRadius: 3,
          background: "#fff",
          border: "1px solid #e0e0e0",
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.04)",
          transition: "all 0.3s ease",
          "&:hover": {
            boxShadow: "0 8px 24px rgba(0, 0, 0, 0.08)",
            transform: "translateY(-2px)",
          },
          direction: themeDirection,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
          <Box>
            <Typography variant="subtitle2" color="text.secondary" fontWeight={600}>
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="caption" sx={{ display: "block", mt: 0.5 }} color="text.secondary">
                {t(subtitle)}
              </Typography>
            )}
          </Box>
          <Box
            sx={{
              backgroundColor: "#4c6ef5",
              borderRadius: "50%",
              p: 0.7,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <AttachMoneyIcon fontSize="small" sx={{ color: "#fff" }} />
          </Box>
        </Box>
        {isLoading ? (
          <CircularProgress size={24} />
        ) : displayItems && displayItems.length > 0 ? (
          displayItems.map((item, idx) => {
            const amount = Number(item.amount);
            const isNegative = amount < 0;
            return (
            <Typography 
                key={idx} 
                variant="h5" 
                fontWeight={700}
                sx={{ 
                  color: isCalculated ? (amount >= 0 ? 'success.main' : 'error.main') : 'text.primary',
                  mb: idx < displayItems.length - 1 ? 1 : 0
                }}
              >
                {isCalculated && amount >= 0 ? '+' : ''}{amount.toFixed(2)} {item.currency}
              </Typography>
            );
          })
        ) : (
          <Typography variant="h6" color="text.secondary">
            0.00
          </Typography>
        )}
      </Card>
    );
  };

  if (isError) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">Error loading dashboard: {error?.message || "Unknown error"}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 4, mt: 4, direction: themeDirection }}>
      <Grid container spacing={2} alignItems="center" justifyContent="space-between" mb={2}>
        <Grid item xs={12} md={6}>
          <Typography variant="h4">{t("dashboard.dashboard")}</Typography>
        </Grid>
        <Grid item xs={12} md={6}>
          <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 2 }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Start date"
                value={startDate}
                onChange={setStartDate}
                slotProps={{
                  textField: {
                    InputLabelProps: { shrink: true },
                    inputProps: { style: { direction: "ltr" } },
                  },
                }}
                sx={{ direction: "ltr" }}
              />
              <DatePicker
                label="End date"
                value={endDate}
                onChange={setEndDate}
                slotProps={{
                  textField: {
                    InputLabelProps: { shrink: true },
                    inputProps: { style: { direction: "ltr" } },
                  },
                }}
                sx={{ direction: "ltr" }}
              />
            </LocalizationProvider>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                setStartDate(null);
                setEndDate(null);
              }}
              sx={{ width: "100px" }}
            >
              {t("dashboard.clearBtn")}
            </Button>
          </Box>
          {dateError && <Typography sx={{ color: "darkred" }}>{dateError}</Typography>}
        </Grid>
      </Grid>

      {/* Main Financial Overview */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "repeat(1, 1fr)", sm: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" },
          gap: 2,
        }}
      >
        {renderSummaryCard("Total Purchase", data?.total_purchases, "Total purchase invoice amount")}
        {renderSummaryCard("Total Invoice Amount", data?.total_sales, "Total sales invoice amount")}
        {renderSummaryCard(t("dashboard.totalIncomes"), data?.total_incomes, "Total income received")}
      </Box>

      {/* Purchase Details */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "repeat(1, 1fr)", sm: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" },
          gap: 2,
        }}
      >
        {renderSummaryCard(t("dashboard.TotalPurchasePaid"), data?.total_purchases_paid, "Amount paid to suppliers")}
        {renderSummaryCard(t("dashboard.TotalPurchaseUnpaid"), data?.total_purchases_unpaid, "Remaining balance to pay")}
        {renderSummaryCard(t("dashboard.TotalPurchaseReturn"), data?.total_purchase_returns, "Purchase returns")}
      </Box>

      {/* Sales Details */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "repeat(1, 1fr)", sm: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" },
          gap: 2,
        }}
      >
        {renderSummaryCard(t("dashboard.TotalSalesPaid"), data?.total_sales_paid, "Amount received from customers")}
        {renderSummaryCard(t("dashboard.TotalSalesUnpaid"), data?.total_sales_unpaid, "Remaining balance to receive")}
        {renderSummaryCard(t("dashboard.TotalReturn"), data?.total_returns, "Sales returns")}
        {renderSummaryCard(t("dashboard.customerOpeningBalance", "Customer opening balance"), data?.customer_opening_balance, "Existing dues from customers (opening balance)")}
      </Box>

      {/* Expenses & Other */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "repeat(1, 1fr)", sm: "repeat(2, 1fr)", lg: "repeat(4, 1fr)" },
          gap: 2,
        }}
      >
        {renderSummaryCard(t("dashboard.payrolls"), data?.total_payrolls, "Total payroll expenses")}
        {renderSummaryCard(t("dashboard.expenses"), data?.total_expenses, "Total business expenses")}
        {renderSummaryCard(t("dashboard.advance"), data?.total_advance, "Advance payments")}
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(1, 1fr)", lg: "repeat(4, 1fr)" }, gap: 3 }}>
        {[
          { title: t("dashboard.PurchaseByCurrency"), data: pieDataFrom("total_purchases") },
          { title: t("dashboard.SalesByCurrency"), data: pieDataFrom("total_sales") },
          { title: t("dashboard.SaleReturnByCurrency"), data: pieDataFrom("total_returns") },
          { title: t("dashboard.PurchaseReturnByCurrency"), data: pieDataFrom("total_purchase_returns") },
        ].map(({ title, data }) => (
          <Card key={title} elevation={3} sx={{ p: 2, borderRadius: 3, boxShadow: "0 4px 20px rgba(0,0,0,0.05)" }}>
            <Typography variant="subtitle1" fontWeight={600} mb={2}>
              {title}
            </Typography>
            <Box sx={{ height: 260 }}>
              <ResponsivePie
                data={data}
                margin={{ top: 10, right: 20, bottom: 40, left: 20 }}
                innerRadius={0.5}
                padAngle={0.7}
                cornerRadius={3}
                colors={COLORS}
                activeOuterRadiusOffset={8}
                borderWidth={1}
                borderColor={{ from: "color", modifiers: [["darker", 0.2]] }}
                legends={[{ anchor: "bottom", direction: "row", translateY: 32, itemWidth: 80, itemHeight: 14 }]}
              />
            </Box>
          </Card>
        ))}
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: "1fr", gap: 3 }}>
        <Card elevation={3} sx={{ p: 2, borderRadius: 3, boxShadow: "0 4px 20px rgba(0,0,0,0.05)" }}>
          <Typography variant="subtitle1" fontWeight={600} mb={2}>
            {t("dashboard.SalesVsPurchases")}
          </Typography>
          <Box sx={{ height: 260 }}>
            <ResponsiveBar
              data={barData}
              keys={["sales", "purchases"]}
              indexBy="currency"
              margin={{ top: 10, right: 20, bottom: 40, left: 60 }}
              padding={0.3}
              axisTop={null}
              axisRight={null}
              axisBottom={{ tickSize: 0, tickPadding: 12 }}
              axisLeft={{ tickSize: 0, tickPadding: 12 }}
              labelSkipWidth={12}
              labelSkipHeight={12}
              legends={[{ anchor: "bottom", direction: "row", translateY: 32 }]}
              colors={{ scheme: "nivo" }}
            />
          </Box>
        </Card>
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: "1fr", gap: 3 }}>
        <Card elevation={3} sx={{ p: 2, borderRadius: 3, boxShadow: "0 4px 20px rgba(0,0,0,0.05)" }}>
          <Typography variant="subtitle1" fontWeight={600} mb={2}>
            Sales Payment vs Purchase Payment
          </Typography>
          <Box sx={{ height: 260 }}>
            <ResponsiveBar
              data={paymentData}
              keys={["sales_paid", "purchases_paid"]}
              indexBy="currency"
              margin={{ top: 10, right: 20, bottom: 40, left: 60 }}
              padding={0.3}
              axisTop={null}
              axisRight={null}
              axisBottom={{ tickSize: 0, tickPadding: 12 }}
              axisLeft={{ tickSize: 0, tickPadding: 12 }}
              labelSkipWidth={12}
              labelSkipHeight={12}
              legends={[{ anchor: "bottom", direction: "row", translateY: 32 }]}
              colors={["#4CAF50", "#FF9800"]}
            />
          </Box>
        </Card>
      </Box>
    </Box>
  );
};

export default Dashboard;
